/**
 * Barbados VAT (17.5%) for ProfitCost and display totals.
 * Ported from DHC lib/barbados-vat.ts. Preserves B-Clean plan logic.
 */

export const BARBADOS_VAT_RATE = 0.175;

/** Official USD→BBD peg (2:1) — applied to landed USD only */
export const USD_BBD_OFFICIAL_PEG = 2;
/** 1.175 × 1.15 — reference for landed USD from base (not multiplied again into BBD) */
export const MANSION_LANDED_PIPELINE = 1.35125;

/** BBD display from already-landed USD (VAT+margin in USD). Peg only — do not re-apply pipeline. */
export function landedBbdFromUsdLanded(landedUsd) {
  const n = Number(landedUsd);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.round(n * USD_BBD_OFFICIAL_PEG * 100) / 100;
}

/**
 * Ticker: landed USD (VAT+margin in US$) | BBD at official peg (×2) on that landed amount.
 */
export function formatLiveMarketInsightsDual(base, margin = 0) {
  const landedUsd = calculateDhcPricing(base, margin);
  const bbd = landedBbdFromUsdLanded(landedUsd);
  return `Live Market Insights: US$${landedUsd.toFixed(2)} | BBD $${bbd.toFixed(2)} (Landed/VAT Inc)`;
}

/** When only bridge `landed_price` (USD) is available — peg ×2 only. */
export function formatLiveMarketInsightsDualFromLandedUsd(landedUsd) {
  const u = Number(landedUsd);
  if (!Number.isFinite(u) || u <= 0) return null;
  const bbd = landedBbdFromUsdLanded(u);
  return `Live Market Insights: US$${u.toFixed(2)} | BBD $${bbd.toFixed(2)} (Landed/VAT Inc)`;
}

/**
 * Add 17.5% Barbados VAT to a base amount. Rounded to 2 decimals.
 */
export function addBarbadosVAT(amount) {
  const total = amount * (1 + BARBADOS_VAT_RATE);
  return Math.round(total * 100) / 100;
}

/**
 * Format a ProfitCost value with Barbados VAT for display.
 * @param {number} profitCost - Base amount before VAT
 * @param {string} [currency='USD']
 * @returns {{ total: number, formatted: string }}
 */
export function formatProfitCostWithVAT(profitCost, currency = 'USD') {
  const total = addBarbadosVAT(profitCost);
  const formatted = new Intl.NumberFormat('en-BB', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(total);
  return { total, formatted };
}

/**
 * DHC consolidated pricing formula:
 * (Base * 1.175) + Margin
 */
export function calculateDhcPricing(base, margin = 0) {
  const vatIncluded = addBarbadosVAT(base);
  const total = vatIncluded + margin;
  return Math.round(total * 100) / 100;
}

/**
 * Format DHC pricing with VAT and margin applied.
 */
export function formatDhcPricing(base, margin = 0, currency = 'USD') {
  const total = calculateDhcPricing(base, margin);
  const formatted = new Intl.NumberFormat('en-BB', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(total);
  return { total, formatted };
}
